import { useRef, useState } from 'react';
import { Ditado } from './Ditado';
import { Alert, Image, Linking, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import type { BlocoPag, Item, Quadro as Q, Traco } from '../store/types';
import { uid } from '../lib/date';
import { abreAqui, abrirAnexo, apagarAnexo, temLocal } from '../lib/anexos';
import { escolherEGuardar } from '../lib/nuvem';
import { useAnexar } from './MenuAnexo';
import { semBloqueio } from '../auth/AuthContext';
import { ActionSheet, Chip, Sheet, type Action } from './ui';
import { QuadroPagina } from './QuadroPagina';
// 0.72 (texto 11b B2): a página com o quadro rola por esta ScrollView para a barra do quadro ficar fixa no topo
export { PaginaRolavel } from './Quadro';
import type { AbaId, Folha } from '../store/types';
import { DesenhoSheet, DesenhoView } from './Desenho';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';
import { oferecerDesfazer } from '../lib/desfazer';
import { nomeAnexo } from '../lib/nomeArquivo';
import { Ordenavel, reordenarParte, semArrastar } from './Arrastar';
import { setAberto, useRecolherTudo } from '../lib/recolher';
import { SemDeslizar } from './ItemDeslizar';
import { useAlvoGuia } from '../lib/guia/destaque';

const item = (): Item => ({ id: uid(), text: '', done: false });
export function novoBlocoPag(tipo: BlocoPag['tipo']): BlocoPag {
  switch (tipo) {
    case 'texto': return { id: uid(), tipo, texto: '' };
    case 'sub': return { id: uid(), tipo, texto: '' };
    case 'check': case 'lista': return { id: uid(), tipo, itens: [item()] };
    case 'tabela': return { id: uid(), tipo, linhas: [['', '', ''], ['', '', ''], ['', '', '']] };
    case 'link': return { id: uid(), tipo, titulo: '', url: '' };
    case 'desenho': return { id: uid(), tipo, tracos: [], alt: 700 };
    default: return { id: uid(), tipo: 'texto', texto: '' };
  }
}

const tamanho = (n?: number) => (n == null ? '' : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const urlOk = (u: string) => /^https?:\/\/\S+\.\S+/i.test(u.trim());

/** Apps para criar documento, planilha ou apresentação (abrem no navegador ou no app instalado). */
const CRIAR: [string, string][] = [
  ['📝 Google Docs', 'https://docs.new'], ['📊 Google Sheets', 'https://sheets.new'], ['📽️ Google Slides', 'https://slides.new'],
  ['📝 Word', 'https://word.new'], ['📊 Excel', 'https://excel.new'], ['📽️ PowerPoint', 'https://powerpoint.new'],
];

/** Moldura de cada bloco (fora do editor para os campos não perderem o foco ao digitar).
 *  Texto 26 (B4, REGRAS 3): sem ⠿ e sem os ícones soltos ⧉ ↑ ✕. O CABEÇALHO (a faixa de cima) é a alça:
 *  segurar e arrastar move o bloco; tocar recolhe / abre (fica só a 1ª linha); ⋮ com Duplicar, Subir, Descer e Excluir.
 *  Deslizar para os lados dentro do bloco não faz nada (3.5). */
function Moldura({ titulo, children, onSubir, onDescer, onRemover, onDuplicar, alca, recolhido, resumo, onMenu, extra }: { titulo?: string; children: React.ReactNode; onSubir: () => void;
  onDescer: () => void; onRemover: () => void; onDuplicar?: () => void; alca?: object; cor?: string; recolhido?: boolean; resumo?: string; sozinho?: boolean;
  onMenu: (titulo: string, a: Action[]) => void; extra?: Action[] }) {
  const menu = () => onMenu(titulo || t('Bloco'), [
    ...(extra ?? []),
    ...(onDuplicar ? [{ label: '⧉ ' + t('Duplicar'), onPress: onDuplicar }] : []),
    { label: '↑ ' + t('Subir'), onPress: onSubir },
    { label: '↓ ' + t('Descer'), onPress: onDescer },
    { label: t('Excluir'), destructive: true, onPress: onRemover },
  ]);
  return (
    <SemDeslizar style={{ backgroundColor: C.card, borderRadius: 14, padding: 10, gap: 6 }}>
      <View {...(alca ?? {})} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 24 }}
        accessibilityLabel={recolhido ? t('Abrir o bloco') : t('Recolher o bloco')}>
        <Text style={{ color: C.mute, fontSize: 11 }}>{recolhido ? '▸' : '▾'}</Text>
        <Text style={{ color: C.sub, fontSize: 11, fontWeight: '700', flex: 1 }} numberOfLines={1}>{titulo ?? ''}</Text>
        <View {...semArrastar}>
          <Pressable hitSlop={10} onPress={menu} accessibilityLabel={t('Opções do bloco')}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800', paddingHorizontal: 4 }}>⋮</Text></Pressable>
        </View>
      </View>
      {recolhido ? <Text style={{ color: C.sub, fontSize: 14 }} numberOfLines={1}>{resumo || '…'}</Text> : children}
    </SemDeslizar>
  );
}

/** Primeira linha do bloco (o que aparece com ele recolhido). */
function primeiraLinha(b: BlocoPag): string {
  switch (b.tipo) {
    case 'texto': case 'sub': return b.texto.split('\n').find(l => l.trim())?.trim() ?? '';
    case 'check': case 'lista': return b.itens.find(i => i.text.trim())?.text.trim() ?? '';
    case 'tabela': return b.linhas.find(l => l.some(c => c.trim()))?.filter(c => c.trim()).join(' | ') ?? '';
    case 'link': return b.titulo.trim() || b.url.trim();
    case 'imagem': case 'arquivo': return nomeAnexo(b.arq);
    case 'desenho': return '✍️ ' + t('Desenho');
    default: return '';
  }
}
/** O bloco tem algo escrito ou colocado (o ✕ só pergunta antes quando tem). */
function temConteudo(b: BlocoPag): boolean {
  switch (b.tipo) {
    case 'texto': case 'sub': return !!b.texto.trim();
    case 'check': case 'lista': return b.itens.some(i => i.text.trim());
    case 'tabela': return b.linhas.some(l => l.some(c => c.trim()));
    case 'link': return !!(b.titulo.trim() || b.url.trim());
    case 'desenho': return b.tracos.length > 0 || !!b.quadro;
    default: return true;
  }
}

/** Editor de página de estudo em blocos: texto, lista, checklist, tabela, link, imagem, arquivo e desenho.
 *  No topo fica o quadro (0.47; padrão das páginas na 0.65). */
export function PaginaEditor({ blocos, onChange, cor, quadro, onQuadro, onRolagem, aba, nome, onAplicarTodas, rotuloTodas, encherTela, soTexto }: { blocos: BlocoPag[]; onChange: (bs: BlocoPag[]) => void; cor: string;
  quadro?: Q | null; onQuadro?: (q: Q) => void; onRolagem?: (pode: boolean) => void;
  aba?: AbaId; nome?: string; onAplicarTodas?: (f: Folha) => void; rotuloTodas?: string; encherTela?: boolean;
  /** só Texto, Checklist e Lista (sem Caneta e sem "＋ Mais") */
  soTexto?: boolean }) {
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [recolhidos, setRecolhidos] = useState<Set<string>>(() => new Set());   // texto 19 (H1b)
  // texto 27 (A2): Subtítulo: tudo abaixo dele, até o próximo, é um grupo que recolhe tocando nele; o app lembra
  const guardado = useRecolherTudo();
  const subAberto = (id: string) => guardado[`sub:${id}`] ?? true;
  const [editSub, setEditSub] = useState<string | null>(null);
  const grupoDe: string[] = (() => { let g = ''; return blocos.map(b => (b.tipo === 'sub' ? (g = b.id) : g)); })();
  const visiveis = blocos.filter((b, i) => b.tipo === 'sub' || !grupoDe[i] || subAberto(grupoDe[i]));
  const resumoGrupo = (sub: string) => {
    const bs = blocos.filter((b, i) => grupoDe[i] === sub && b.tipo !== 'sub');
    const ch = bs.flatMap(b => (b.tipo === 'check' ? b.itens : [])).filter(i => i.text.trim());
    if (ch.length) return t('{a} de {n} feitos', { a: ch.filter(i => i.done).length, n: ch.length });
    const li = bs.flatMap(b => (b.tipo === 'lista' ? b.itens : [])).filter(i => i.text.trim());
    if (li.length) return t('{n} item(ns)', { n: li.length });
    return bs.length ? primeiraLinha(bs[0]) : '';
  };
  const virar = (id: string, tipo: 'sub' | 'texto') => set(bs => bs.map(b => (b.id === id && (b.tipo === 'texto' || b.tipo === 'sub')
    ? { id: b.id, tipo, texto: tipo === 'sub' ? b.texto.split('\n').map(x => x.trim()).filter(Boolean).join(' ') : b.texto } : b)));
  const [desenho, setDesenho] = useState<{ id: string | null; tracos: Traco[]; alt: number; larg?: number; quadro?: Q | null } | null>(null);
  const refs = useRef<Record<string, TextInput | null>>({});
  const atual = useRef(blocos); atual.current = blocos;
  const alvoBlocos = useAlvoGuia('estudos.blocos');
  const anexoMenu = useAnexar({ cor });   // guia (texto 08, C1): a fileira de blocos, só nas páginas de Estudos

  const set = (fn: (bs: BlocoPag[]) => BlocoPag[]) => onChange(fn(atual.current));
  const mudar = (id: string, fn: (b: any) => BlocoPag) => set(bs => bs.map(b => (b.id === id ? fn(b) : b)));
  const add = (b: BlocoPag) => set(bs => [...bs, b]);
  const remover = (b: BlocoPag) => {
    // texto 05 C1: "🗑️ Item excluído · Desfazer" por 5 segundos (o bloco volta no mesmo lugar). O arquivo da imagem
    // ou do anexo só é apagado depois que o Desfazer some.
    const tira = () => {
      const pos = atual.current.findIndex(x => x.id === b.id);
      let voltou = false;
      set(bs => bs.filter(x => x.id !== b.id));
      if (b.tipo === 'imagem' || b.tipo === 'arquivo') setTimeout(() => { if (!voltou) apagarAnexo(b.arq); }, 6000);
      oferecerDesfazer('excluir', () => {
        voltou = true;
        set(bs => (bs.some(x => x.id === b.id) ? bs : [...bs.slice(0, Math.max(0, Math.min(pos, bs.length))), b, ...bs.slice(Math.max(0, Math.min(pos, bs.length)))]));
      });
    };
    // texto 19 (H1): o ✕ pergunta antes só quando o bloco tem conteúdo (vazio sai direto, com o Desfazer)
    if (!temConteudo(b)) { tira(); return; }
    Alert.alert(t('Excluir este bloco?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: tira }]);
  };
  /** Cópia do bloco logo abaixo (imagens e arquivos não, para não dividir o mesmo arquivo entre dois blocos). */
  const duplicar = (b: BlocoPag) => set(bs => {
    const i = bs.findIndex(x => x.id === b.id); if (i < 0) return bs;
    const c = JSON.parse(JSON.stringify(b)) as BlocoPag; c.id = uid();
    if ('itens' in c) c.itens = c.itens.map(it => ({ ...it, id: uid() }));
    const a = [...bs]; a.splice(i + 1, 0, c); return a;
  });
  const acoes = (b: BlocoPag, al?: object) => ({ onSubir: () => subir(b.id), onDescer: () => descer(b.id), onRemover: () => remover(b), alca: al, cor,
    onMenu: (tt: string, a: Action[]) => setMenu({ t: tt, a }),
    recolhido: recolhidos.has(b.id), resumo: primeiraLinha(b), sozinho: blocos.length < 2,
    onDuplicar: b.tipo === 'imagem' || b.tipo === 'arquivo' ? undefined : () => duplicar(b) });
  const descer = (id: string) => set(bs => { const i = bs.findIndex(b => b.id === id); if (i < 0 || i >= bs.length - 1) return bs; const a = [...bs]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; return a; });
  const subir = (id: string) => set(bs => { const i = bs.findIndex(b => b.id === id); if (i < 1) return bs; const a = [...bs]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; });

  // 0.71 (texto 09 C1/C2): imagem pelo menu Fotos · Tirar foto · Arquivos; arquivo direto nos Arquivos
  async function anexar(imagem: boolean) {
    try {
      const r0 = imagem ? await anexoMenu.anexar({ tipo: 'imagem', titulo: t('Inserir imagem') }) : null;
      const r = imagem ? (r0 ? { arq: r0.anexo, aviso: r0.aviso } : null) : await escolherEGuardar(false);
      if (r) add({ id: uid(), tipo: imagem ? 'imagem' : 'arquivo', arq: r.arq });
      if (r?.aviso) Alert.alert(t('Arquivo adicionado'), r.aviso);
    } catch (e: any) { Alert.alert(t('Não foi possível'), String(e?.message ?? e)); }
  }
  const abrir = (fn: () => Promise<unknown>) => semBloqueio(fn as any).catch((e: any) => Alert.alert(t('Não foi possível abrir'), String(e?.message ?? e)));

  const menuAdicionar = () => setMenu({ t: t('Adicionar na página'), a: [
    { label: '✏️ ' + t('Texto'), onPress: () => add(novoBlocoPag('texto')) },
    { label: '• ' + t('Lista'), onPress: () => add(novoBlocoPag('lista')) },
    { label: '☑️ ' + t('Checklist'), onPress: () => add(novoBlocoPag('check')) },
    { label: '▦ ' + t('Tabela'), onPress: () => add(novoBlocoPag('tabela')) },
    { label: '🔗 ' + t('Link'), onPress: () => add(novoBlocoPag('link')) },
    { label: '🖼️ ' + t('Imagem'), onPress: () => anexar(true) },
    { label: '📎 ' + t('Arquivo (Drive, OneDrive, Dropbox ou do dispositivo)'), onPress: () => anexar(false) },
    { label: '✍️ ' + t('Desenho com caneta ou dedo'), onPress: () => setDesenho({ id: null, tracos: [], alt: 700 }) },
    { label: '📄 ' + t('Criar documento, planilha ou apresentação'), onPress: () => setTimeout(menuCriar, 300) },
  ] });
  const menuCriar = () => setMenu({ t: t('Criar em outro app'), a: CRIAR.map(([n, url]) => ({ label: n, onPress: () => {
    add({ id: uid(), tipo: 'link', titulo: n.slice(n.indexOf(' ') + 1), url: '' });
    abrir(() => Linking.openURL(url));
    setTimeout(() => Alert.alert(t('Depois de criar'), t('No outro app, copie o link do arquivo (Compartilhar › Copiar link) e cole no bloco de link que ficou nesta página.')), 600);
  } })) });

  return (
    <View style={{ gap: 10 }}>
      {onQuadro && <QuadroPagina valor={quadro} onChange={onQuadro} cor={cor} aba={aba} nome={nome} onRolagem={onRolagem} onAplicarTodas={onAplicarTodas} rotuloTodas={rotuloTodas} encherTela={encherTela} />}
      <Ordenavel itens={visiveis} chave={b => b.id} alca alcaImediata gap={10} onOrdem={l => set(bs => reordenarParte(bs, l, b => b.id))}
        onToqueAlca={k => {
          if (blocos.find(b => b.id === k)?.tipo === 'sub') return setAberto(`sub:${k}`, !subAberto(k));
          setRecolhidos(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
        }} render={(b, _i, ord) => {
        const al = ord.alca;
        switch (b.tipo) {
          case 'sub': {
            const ab = subAberto(b.id), editando = editSub === b.id || !b.texto.trim(), resumo = ab ? '' : resumoGrupo(b.id);
            return (
              <SemDeslizar>
                <View {...al} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }} accessibilityLabel={ab ? t('Recolher o grupo') : t('Abrir o grupo')}>
                  <Text style={{ color: C.sub, fontSize: 13 }}>{ab ? '▾' : '▸'}</Text>
                  {editando ? (
                    <TextInput {...semArrastar} value={b.texto} autoFocus={editSub === b.id} onBlur={() => setEditSub(null)} onSubmitEditing={() => setEditSub(null)}
                      onChangeText={v => mudar(b.id, x => ({ ...x, texto: v }))} placeholder={t('Subtítulo')} placeholderTextColor={C.mute}
                      style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 19, fontWeight: '800', padding: 0 }} />
                  ) : (
                    <Text style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 19, fontWeight: '800' }} numberOfLines={ab ? undefined : 1}>
                      {b.texto}{resumo ? <Text style={{ color: C.sub, fontSize: 12, fontWeight: '400' }}>  · {resumo}</Text> : null}</Text>
                  )}
                  <View {...semArrastar}>
                    <Pressable hitSlop={10} accessibilityLabel={t('Opções do bloco')} onPress={() => setMenu({ t: t('Subtítulo'), a: [
                      { label: '✏️ ' + t('Editar subtítulo'), onPress: () => { setAberto(`sub:${b.id}`, true); setEditSub(b.id); } },
                      { label: '¶ ' + t('Transformar em texto'), onPress: () => virar(b.id, 'texto') },
                      { label: '↑ ' + t('Subir'), onPress: () => subir(b.id) },
                      { label: '↓ ' + t('Descer'), onPress: () => descer(b.id) },
                      { label: t('Excluir'), destructive: true, onPress: () => remover(b) },
                    ] })}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800', paddingHorizontal: 4 }}>⋮</Text></Pressable>
                  </View>
                </View>
              </SemDeslizar>
            );
          }
          case 'texto': return (
            <Moldura key={b.id} titulo={t('Texto')} {...acoes(b, al)} extra={[{ label: 'H ' + t('Transformar em subtítulo'), onPress: () => virar(b.id, 'sub') }]}>
              {/* o 🎤 fica no canto do próprio campo (sem linha a mais), para a caixa ter o tamanho de antes da 0.65 */}
              <View>
                <TextInput multiline value={b.texto} onChangeText={v => mudar(b.id, x => ({ ...x, texto: v }))} placeholder={t('Escreva o resumo, tópicos, dúvidas...')}
                  placeholderTextColor={C.mute} textAlignVertical="top" style={{ color: C.text, fontSize: 16, lineHeight: 23, minHeight: 90, paddingRight: 36 }} />
                <View style={{ position: 'absolute', right: 0, bottom: 0 }}><Ditado pequeno cor={cor} onTexto={tx => mudar(b.id, x => ({ ...x, texto: x.texto ? x.texto + ' ' + tx : tx }))} /></View>
              </View>
            </Moldura>
          );
          case 'check': case 'lista': return (
            <Moldura key={b.id} titulo={b.tipo === 'check' ? t('Checklist') : t('Lista')} {...acoes(b, al)}>
              {b.itens.map((it, i) => (
                <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  {b.tipo === 'check' ? (
                    <Pressable hitSlop={8} onPress={() => mudar(b.id, x => ({ ...x, itens: x.itens.map((y: Item) => (y.id === it.id ? { ...y, done: !y.done } : y)) }))}
                      style={{ width: 20, height: 20, borderRadius: 6, borderWidth: 2, borderColor: it.done ? cor : C.mute, backgroundColor: it.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                      {it.done && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}
                    </Pressable>
                  ) : <Text style={{ color: cor, fontSize: 18, width: 20, textAlign: 'center' }}>•</Text>}
                  <TextInput ref={r => { refs.current[it.id] = r; }} value={it.text} placeholder={t('Item')} placeholderTextColor={C.mute} blurOnSubmit={false} returnKeyType="next"
                    style={{ flex: 1, minWidth: 0, color: C.text, paddingVertical: 6, textDecorationLine: it.done ? 'line-through' : 'none', opacity: it.done ? 0.6 : 1 }}
                    onChangeText={v => mudar(b.id, x => ({ ...x, itens: x.itens.map((y: Item) => (y.id === it.id ? { ...y, text: v } : y)) }))}
                    onSubmitEditing={() => { const n = item(); mudar(b.id, x => { const a = [...x.itens]; a.splice(i + 1, 0, n); return { ...x, itens: a }; }); setTimeout(() => refs.current[n.id]?.focus(), 60); }}
                    onKeyPress={e => { if (e.nativeEvent.key === 'Backspace' && !it.text && b.itens.length > 1) mudar(b.id, x => ({ ...x, itens: x.itens.filter((y: Item) => y.id !== it.id) })); }} />
                </View>
              ))}
            </Moldura>
          );
          case 'tabela': {
            const cols = b.linhas[0]?.length ?? 0;
            const setCel = (r: number, c: number, v: string) => mudar(b.id, x => ({ ...x, linhas: x.linhas.map((l: string[], i: number) => (i === r ? l.map((y, j) => (j === c ? v : y)) : l)) }));
            return (
              <Moldura key={b.id} titulo={t('Tabela')} {...acoes(b, al)}>
                <ScrollView horizontal nestedScrollEnabled>
                  <View>
                    {b.linhas.map((l, r) => (
                      <View key={r} style={{ flexDirection: 'row' }}>
                        {l.map((v, c) => (
                          <TextInput key={c} value={v} onChangeText={x => setCel(r, c, x)} multiline placeholderTextColor={C.mute}
                            style={{ width: 120, minHeight: 38, color: C.text, borderWidth: 1, borderColor: C.line, padding: 6, fontWeight: r === 0 ? '700' : '400',
                              backgroundColor: r === 0 ? alpha(cor, 0.12) : 'transparent', fontSize: 13 }} />
                        ))}
                      </View>
                    ))}
                  </View>
                </ScrollView>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                  <Text style={{ color: cor, fontSize: 12 }} onPress={() => mudar(b.id, x => ({ ...x, linhas: [...x.linhas, Array(cols).fill('')] }))}>+ {t('linha')}</Text>
                  <Text style={{ color: cor, fontSize: 12 }} onPress={() => mudar(b.id, x => ({ ...x, linhas: x.linhas.map((l: string[]) => [...l, '']) }))}>+ {t('coluna')}</Text>
                  {b.linhas.length > 1 && <Text style={{ color: C.sub, fontSize: 12 }} onPress={() => mudar(b.id, x => ({ ...x, linhas: x.linhas.slice(0, -1) }))}>− {t('linha')}</Text>}
                  {cols > 1 && <Text style={{ color: C.sub, fontSize: 12 }} onPress={() => mudar(b.id, x => ({ ...x, linhas: x.linhas.map((l: string[]) => l.slice(0, -1)) }))}>− {t('coluna')}</Text>}
                </View>
              </Moldura>
            );
          }
          case 'link': return (
            <Moldura key={b.id} titulo={'🔗 ' + t('Link')} {...acoes(b, al)}>
              <TextInput value={b.titulo} onChangeText={v => mudar(b.id, x => ({ ...x, titulo: v }))} placeholder={t('Nome (opcional)')} placeholderTextColor={C.mute}
                style={{ color: C.text, fontWeight: '700', paddingVertical: 4 }} />
              <TextInput value={b.url} onChangeText={v => mudar(b.id, x => ({ ...x, url: v.trim() }))} placeholder={t('Cole o link (https://...)')} placeholderTextColor={C.mute}
                autoCapitalize="none" autoCorrect={false} keyboardType="url" style={{ color: cor, paddingVertical: 4, fontSize: 13 }} />
              {urlOk(b.url) && <Text style={{ color: cor, fontWeight: '700' }} onPress={() => abrir(() => Linking.openURL(b.url))}>{t('Abrir')} ↗</Text>}
            </Moldura>
          );
          case 'imagem': {
            const ok = temLocal(b.arq), src = b.arq.dado ?? b.arq.uri;
            return (
              <Moldura key={b.id} titulo={'🖼️ ' + nomeAnexo(b.arq)} {...acoes(b, al)}>
                {ok && src ? (
                  <Pressable onPress={() => abrir(() => abrirAnexo(b.arq))}>
                    <Image source={{ uri: src }} style={{ width: '100%', height: 220, borderRadius: 10, backgroundColor: C.card2 }} resizeMode="contain" />
                  </Pressable>
                ) : b.arq.url ? (
                  <Pressable onPress={() => abrir(() => abrirAnexo(b.arq))}>
                    <Text style={{ color: C.sub, fontSize: 12 }}>☁️ {t('Imagem guardada no {nome}. Toque para abrir.', { nome: b.arq.nuvem?.p === 'microsoft' ? 'OneDrive' : 'Google Drive' })}</Text>
                  </Pressable>
                ) : <Text style={{ color: C.sub, fontSize: 12 }}>{t('Esta imagem foi guardada em outro dispositivo.')}</Text>}
              </Moldura>
            );
          }
          case 'arquivo': {
            const ok = abreAqui(b.arq);
            return (
              <Moldura key={b.id} titulo={'📎 ' + t('Arquivo')} {...acoes(b, al)}>
                <Pressable onPress={() => abrir(() => abrirAnexo(b.arq))} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <Text style={{ fontSize: 26 }}>📎</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={2}>{nomeAnexo(b.arq)}</Text>
                    <Text style={{ color: C.sub, fontSize: 11 }}>{ok ? [tamanho(b.arq.tamanho), t('Toque para abrir')].filter(Boolean).join(' · ') : t('Guardado em outro dispositivo')}</Text>
                  </View>
                </Pressable>
              </Moldura>
            );
          }
          case 'desenho': return (
            <Moldura key={b.id} titulo={'✍️ ' + t('Desenho')} {...acoes(b, al)}>
              <Pressable onPress={() => setDesenho({ id: b.id, tracos: b.tracos, alt: b.alt, larg: b.larg, quadro: b.quadro })}><DesenhoView tracos={b.tracos} alt={b.alt} larg={b.larg} quadro={b.quadro} /></Pressable>
              <Text style={{ color: C.mute, fontSize: 11 }}>{t('Toque no desenho para continuar desenhando.')}</Text>
            </Moldura>
          );
        }
      }} />

      <View ref={aba === 'estudos' ? alvoBlocos.ref : undefined} collapsable={false} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {([['texto', '✏️ ' + t('Texto')], ['sub', 'H ' + t('Subtítulo')], ['check', '☑️ ' + t('Checklist')], ['lista', '• ' + t('Lista')]] as const).map(([k, l]) => (
          <Pressable key={k} onPress={() => add(novoBlocoPag(k))} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 14, backgroundColor: C.card2 }}>
            <Text style={{ color: C.text, fontSize: 13 }}>{l}</Text>
          </Pressable>
        ))}
        {!soTexto && <Pressable onPress={() => setDesenho({ id: null, tracos: [], alt: 700 })} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 14, backgroundColor: C.card2 }}>
          <Text style={{ color: C.text, fontSize: 13 }}>✍️ {t('Caneta')}</Text>
        </Pressable>}
        {!soTexto && <Pressable onPress={menuAdicionar} style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 14, backgroundColor: alpha(cor, 0.25) }}>
          <Text style={{ color: C.text, fontSize: 13, fontWeight: '700' }}>＋ {t('Mais')}</Text>
        </Pressable>}
      </View>

      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      <DesenhoSheet visible={!!desenho} inicial={desenho?.tracos} inicialQ={desenho?.quadro} alt={desenho?.alt} larg={desenho?.larg} cor={cor} aba={aba} onClose={() => setDesenho(null)}
        onSalvar={(tracos, alt, larg, quadro) => {
          if (desenho?.id) mudar(desenho.id, x => ({ ...x, tracos, alt, larg, quadro }));
          else if (tracos.length || quadro) add({ id: uid(), tipo: 'desenho', tracos, alt, larg, quadro });
          setDesenho(null);
        }} />
      {anexoMenu.tela}
    </View>
  );
}
